import { useState, useEffect } from "react";

export const RECOVERY_COOLDOWN_KEY = "password_recovery_cooldown";
export const DEFAULT_COOLDOWN_SECONDS = 60;

export function getRecoveryCooldown(email) {
  try {
    const raw = localStorage.getItem(RECOVERY_COOLDOWN_KEY);
    if (!raw) return 0;
    const data = JSON.parse(raw);
    if (!data || !data.expiresAt) return 0;

    if (
      email &&
      data.email &&
      String(data.email).trim().toLowerCase() !== String(email).trim().toLowerCase()
    ) {
      return 0;
    }

    const remaining = Math.ceil((data.expiresAt - Date.now()) / 1000);
    if (remaining <= 0) {
      localStorage.removeItem(RECOVERY_COOLDOWN_KEY);
      return 0;
    }
    return remaining;
  } catch {
    return 0;
  }
}

export function setRecoveryCooldown(email, seconds = DEFAULT_COOLDOWN_SECONDS) {
  try {
    const payload = {
      email: String(email || "").trim().toLowerCase(),
      expiresAt: Date.now() + seconds * 1000,
    };
    localStorage.setItem(RECOVERY_COOLDOWN_KEY, JSON.stringify(payload));
  } catch {
    // noop
  }
}

export function clearRecoveryCooldown() {
  try {
    localStorage.removeItem(RECOVERY_COOLDOWN_KEY);
  } catch {
    // noop
  }
}

export function useRecoveryCooldown(email) {
  const [countdown, setCountdown] = useState(() => getRecoveryCooldown(email));

  // Sync state whenever email changes
  useEffect(() => {
    setCountdown(getRecoveryCooldown(email));
  }, [email]);

  // Start interval whenever countdown > 0
  useEffect(() => {
    if (countdown <= 0) return;

    const interval = setInterval(() => {
      const current = getRecoveryCooldown(email);
      setCountdown(current);
      if (current <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [email, countdown > 0]);

  const startCooldown = (seconds = DEFAULT_COOLDOWN_SECONDS, targetEmail = email) => {
    setRecoveryCooldown(targetEmail, seconds);
    setCountdown(seconds);
  };

  return {
    countdown,
    startCooldown,
    isCoolingDown: countdown > 0,
  };
}
